// @ts-nocheck
import { formGlobals } from './_/form-globals';
import BaseInput from './_/base-input';

/**
 * Class: TextInput
 * Purpose: Manage a single text/textarea input, track validity and emit custom events on changes.
 * Constructor Parameters (types):
 *   wrap (HTMLElement)     - Container element that wraps the input + error UI.
 *   formIndex (number|null)- Index of the parent form instance.
 *   wrapIndex (number|null)- Index of this field within the form instance.
 *
 * Public Methods:
 *   isValid(): boolean
 *     // Returns current validity state.
 *
 *   getState(): { value:string, valid:boolean, blurred:boolean, required:boolean, id:string }
 *     // Returns the current state snapshot.
 *
 *   validateNow(): void
 *     // Forces validation against current value.
 *
 *   destroy(): void
 *     // Removes listeners and references.
 *
 * Events:
 *   'validchange' (detail:{ valid:boolean, value:string, instance:TextInput })
 *   'valuechange' (detail:{ value:string, instance:TextInput })
 *   'blur'        (detail:{ instance:TextInput })
 *   'focus'       (detail:{ instance:TextInput })
 *
 * Notes:
 * - Events bubble and are composed, so parent controllers can listen without holding references.
 * - Validators are pluggable via TextInput.validators[validateKey].
 * 
 * @class
 * @extends {BaseInput}
 * @this {TextInput}
 */
export default class TextInput extends BaseInput {
  /**
   * @param {HTMLElement} wrap
   * @param {number|null} formIndex
   * @param {number|null} wrapIndex
   */
  constructor(wrap, formIndex = null, wrapIndex = null) {
    super(wrap, formIndex, wrapIndex);

    /** @type {HTMLInputElement|HTMLTextAreaElement|null} */
    this.input = wrap.querySelector('input[type="text"], textarea');
    this.inputId = `form-${formIndex}-input-${wrapIndex}`;

    // Initialize base class elements
    this._initErrorElement();
    this._initNoteElement(this.wrap.querySelector(formGlobals.noteSelector) || null);

    // config
    this.required = this.input?.getAttribute('aria-required') === 'true';
    this.validateKey = this.input?.getAttribute('data-validate') || null;

    // state
    this.blurred = false;
    this.value = this.input?.value || '';
    this.valid = this.required ? false : true;

    // bindings
    this._onFocus = this._onFocus.bind(this);
    this._onBlur = this._onBlur.bind(this);
    this._onInput = this._onInput.bind(this);

    this._setAttributes();
    this._initEvents();
  }

  /** @returns {HTMLInputElement|HTMLTextAreaElement|null} */
  _getAriaElement() {
    return this.input;
  }

  _getValidChangeDetail(): { valid: boolean; value: string; instance: TextInput } {
    return {
      valid: this.valid,
      value: this.value,
      instance: this
    };
  }

  /** @returns {boolean} */
  isValid() {
    return this.valid;
  }

  /** @returns {{value:string, valid:boolean, blurred:boolean, required:boolean, id:string}} */
  getState() {
    return {
      value: this.value,
      valid: this.valid,
      blurred: this.blurred,
      required: this.required,
      id: this.inputId
    };
  }

  /** Force validation against current value. */
  validateNow() {
    this._validate(this.input?.value ?? '');
  }

  /** Remove listeners and references. */
  destroy() {
    super.destroy();
    this.input = null;
  }

  // ---------- Private ----------
  /**
   * Function: _setAttributes
   * Purpose: Initialize IDs and initial aria-describedby based on note/error presence.
   * Params: none
   */
  _setAttributes() {
    if (!this.input) return;

    this.input.setAttribute('id', this.inputId);

    // Initial describedby: prefer note only (no error at start)
    this._setAriaDescribedBy({ includeNote: true, includeError: false });
  }

  /**
   * Function: _initEvents
   * Purpose: Attach DOM listeners with AbortController for clean teardown.
   * Params: none
   */
  _initEvents() {
    if (!this.input) return;

    const o = { signal: this._ac.signal };

    this.input.addEventListener('focus', this._onFocus, o);
    this.input.addEventListener('blur', this._onBlur, o);
    this.input.addEventListener('input', this._onInput, o);
  }

  _onFocus() {
    this.wrap.classList.add(formGlobals.focusedClass);
    this._dispatch('focus', { instance: this });
  }

  _onBlur() {
    this.wrap.classList.remove(formGlobals.focusedClass);
    const wasFirst = !this.blurred;
    this.blurred = true;
    this._dispatch('blur', { instance: this });

    // Validate on first blur if required
    if (wasFirst && this.required) {
      this._validate(this.input.value);
    }
  }

  _onInput() {
    this.value = this.input.value;

    if (this.value) {
      this.wrap.classList.add(formGlobals.filledClass);
    } else {
      this.wrap.classList.remove(formGlobals.filledClass);
    }

    this._dispatch('valuechange', { value: this.value, instance: this });

    if (this.blurred && this.required) {
      this._validate(this.value);
    }
  }

  /**
   * Function: _validate
   * Purpose: Run validation either via validator registry or default required rule.
   * Params:
   *   value (string) - raw input value
   */
  _validate(value) {
    let isValid = false;

    if (this.validateKey && typeof TextInput.validators?.[this.validateKey] === 'function') {
      isValid = !!TextInput.validators[this.validateKey](value);
    } else {
      // Default: non-empty if required, otherwise always valid
      isValid = this.required ? value.trim() !== '' : true;
    }

    if (isValid) {
      this._setValid();
    } else {
      this._setInvalid();
    }
  }

}

/**
 * Static validators registry: plug in your rules by key.
 * All functions should accept (value:string) and return boolean.
 */
TextInput.validators = {
  // example validators:
  nonEmpty: (v) => v.trim().length > 0,
  alpha: (v) => /^[A-Za-zÄÖÜäöüß\s'-]+$/.test(v),
  min3: (v) => v.trim().length >= 3,
};
